import {
  createNewConversation,
  postUserMessage,
} from "@mirage/service-conversation";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import { convertToChatMessage } from "@mirage/service-conversation/utils/convertToChatMessage";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { ChatConversationMessage } from "@mirage/mosaics/Chat/types";
import type {
  ConversationMessage,
  ConversationSuggestedQuery,
} from "@mirage/service-conversation/types";

/**
 * This hook is used to initiate new conversation on each new query.
 */
export const useInitialConversation = (query: string, isEnabled: boolean) => {
  const [conversationId, setConversationId] = useState<string>();

  const conversation = useConversation(conversationId);

  const composeMessages = useMemo(
    () => conversation?.messages?.map(convertToChatMessage) || [],
    [conversation?.messages],
  );

  const isWaitingForResponse = useMemo(() => {
    return (
      conversation === undefined ||
      conversation.messages?.length === 0 ||
      conversation.messages?.some((message) => message.status === "pending")
    );
  }, [conversation]);

  const startNewConversation = useCallback(async (query: string) => {
    const newConversation = await createNewConversation();
    setConversationId(newConversation.id);

    postUserMessage({
      conversationId: newConversation.id,
      message: query,
      source: "serp-compose_chat_pane",
    });
  }, []);

  const postFollowUpMessage = useCallback(
    async (query: string) => {
      if (conversationId) {
        postUserMessage({
          conversationId,
          message: query,
          source: "serp-compose_chat_pane",
        });
      }
    },
    [conversationId],
  );

  // If flag is on, initiate a new conversation on each new query (unless filters are enabled)
  useEffect(() => {
    if (isEnabled && query) {
      startNewConversation(query);
    }
  }, [query, startNewConversation, isEnabled]);

  const loadingState: LoadingState = useMemo(() => {
    if (!isEnabled) {
      return "done";
    }
    return getLoadingState(conversation?.messages || []);
  }, [isEnabled, conversation]);

  return {
    messages: composeMessages,
    suggestedQueries: conversation?.suggestedQueries || [],
    conversationId,
    isWaitingForResponse,
    postFollowUpMessage,
    loadingState,
  };
};

function getLoadingState(messages: ConversationMessage[]): LoadingState {
  if (messages.length === 0) {
    return "initiating";
  }
  // Look for an initial response -- first message from Assistant
  const firstAssistantMessage = messages.find(
    (message) => message.messageType.case === "assistantMessage",
  );
  if (!firstAssistantMessage) {
    return "initiating"; // waiting for assistant message
  }
  switch (firstAssistantMessage.status) {
    case "pending":
      return "initiating";
    case "streaming":
      return "streaming";
    case "success":
      return "done";
    case "error":
      return "error";
    default:
      firstAssistantMessage.status satisfies never;
      throw new Error(
        `Unknown message status: ${firstAssistantMessage.status}`,
      );
  }
}

export type LoadingState = "initiating" | "streaming" | "done" | "error";

export type AnswerConversationProps =
  | {
      isEnabled: true;
      messages: ChatConversationMessage[]; // empty when loading
      suggestedQueries: ConversationSuggestedQuery[];
      conversationId: string | undefined; // undefined when loading
      /**
       * Post a follow up message to the conversation
       * @returns A promise that resolves when the message is posted (does NOT wait for its response)
       */
      postFollowUpMessage: (query: string) => Promise<void>;
      loadingState: LoadingState;
    }
  | {
      isEnabled: false;
    };

export function useAnswerConversationProps(
  isEnabled: boolean,
  initialConversation: ReturnType<typeof useInitialConversation>,
): AnswerConversationProps {
  return useMemo(() => {
    if (isEnabled) {
      return {
        ...initialConversation,
        isEnabled,
      };
    }
    return {
      isEnabled: false,
    };
  }, [initialConversation, isEnabled]);
}
